import React, {useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useHistory, Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import * as S from "./styles";
import CustomButton from "../../components/Form/Button";
import {Form} from "@unform/web";
import InputMask from "../../components/Form/InputMask";
import logo from "../../assets/img/logo.png";
import {toast} from "react-toastify";
import messageTypes from "../../constants/MessagesTypes";
import * as Yup from "yup";
import {CircularProgress} from "@material-ui/core";
import {newSignaturePaymentPix, newSignaturePaymentCreditCard, getSignature} from "../../services/Profile";
import DivDiagonal from "../../components/DivDiagonal";
import {performancePrimaryButtonStyle} from "../Report/Performance/styles";

const PlansPayment = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const [paymentMethod, setPaymentMethod] = useState("PIX");
    const [loading, setLoading] = useState(false);
    const [dataQRCode, setDataQRCode] = useState(null);
    const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
    const formPixRef = useRef(null);
    const formCardRef = useRef(null);

    const query = new URLSearchParams(location.search);
    const type = query.get("type");

    const isValidPlan = type === "SEMIANNUAL" || type === "YEARLY";

    const planSummary = useMemo(() => {
        if (type === "SEMIANNUAL") {
            return t("plans_payment_summary_semestral");
        }
        if (type === "YEARLY") {
            return t("plans_payment_summary_yearly");
        }
        return t("plans_payment_summary_fallback");
    }, [type, t]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const buttonHoverIn = (e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(242, 146, 5, 0.45)";
    };
    const buttonHoverOut = (e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = performancePrimaryButtonStyle.boxShadow;
    };

    const handleSubmitPaymentPix = async (data) => {
        try {
            if (!type || !isValidPlan) {
                toast.warning(t("plans_payment_invalid"));
                return;
            }

            setLoading(true);
            if (formPixRef.current) {
                formPixRef.current.setErrors({});
            }

            const schema = Yup.object().shape({
                cpfCnpj: Yup.string().required(t("input_required")),
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            const formData = new FormData();
            formData.append("type", type);
            formData.append("cpfCnpj", data.cpfCnpj);

            const response = await newSignaturePaymentPix(formData);
            setDataQRCode(response.data);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            if (err instanceof Yup.ValidationError) {
                const validationErrors = {};
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                if (formPixRef.current) {
                    formPixRef.current.setErrors(validationErrors);
                }
                toast.warning(messageTypes.REQUIRED_FIELDS);
                return;
            }

            const messageError = err?.response?.data?.messageError;
            if (messageError) {
                toast.error(messageError);
            } else {
                toast.error(messageTypes.ERROR);
            }
        }
    };

    useEffect(() => {
        if (!dataQRCode) {
            return;
        }

        const intervalID = setInterval(() => {
            getSignature().then((response) => {
                const data = response && response.data ? response.data : {};
                if (data.isSignatureActive) {
                    clearInterval(intervalID);
                    setIsPaymentConfirmed(true);
                    setTimeout(() => {
                        history.push("/dashboard");
                    }, 3000);
                }
            });
        }, 5000);

        return () => clearInterval(intervalID);
    }, [dataQRCode, history]);

    return (
        <S.PlansPaymentLayout>
            <DivDiagonal mobileHeight={260} />
            <S.PlansPaymentContent>
                <S.PaymentContentShell>
                <S.PaymentPageInner>
                    <S.PaymentCard>
                        <S.PaymentLogoWrap>
                            <img src={logo} alt="GBinary" width={132} height="auto"/>
                        </S.PaymentLogoWrap>

                        {!isValidPlan && !dataQRCode ? (
                            <S.PaymentInvalidBlock>
                                <p>{t("plans_payment_invalid")}</p>
                                <Link
                                    to="/plans"
                                    style={{
                                        textDecoration: "none",
                                        display: "block",
                                        width: "100%",
                                    }}
                                >
                                    <CustomButton
                                        fullWidth
                                        type="button"
                                        style={performancePrimaryButtonStyle}
                                        onMouseEnter={buttonHoverIn}
                                        onMouseLeave={buttonHoverOut}
                                    >
                                        {t("plans_payment_back_plans")}
                                    </CustomButton>
                                </Link>
                            </S.PaymentInvalidBlock>
                        ) : isPaymentConfirmed ? (
                            <S.PaymentSuccessBlock>
                                <h3>{t("plans_payment_success_title")}</h3>
                                <p>{t("plans_payment_success_sub")}</p>
                                <CircularProgress style={{color: "#f29205"}}/>
                            </S.PaymentSuccessBlock>
                        ) : dataQRCode ? (
                            <S.PaymentQrSection>
                                <h3>{t("plans_payment_qr_title")}</h3>
                                <S.PaymentQrFrame>
                                    <img
                                        src={`data:image/png;base64,${dataQRCode.encodedImage}`}
                                        alt="QR Code Pix"
                                    />
                                </S.PaymentQrFrame>
                                <p>{t("plans_payment_copy_hint")}</p>
                                <CustomButton
                                    fullWidth
                                    style={performancePrimaryButtonStyle}
                                    onMouseEnter={buttonHoverIn}
                                    onMouseLeave={buttonHoverOut}
                                    onClick={() => {
                                        navigator.clipboard.writeText(dataQRCode.payload);
                                        toast.success(t("plans_payment_copy_toast"));
                                    }}
                                >
                                    {t("plans_payment_copy_btn")}
                                </CustomButton>
                                <S.PaymentStepsList>
                                    <li>{t("plans_payment_step_1")}</li>
                                    <li>{t("plans_payment_step_2")}</li>
                                    <li>{t("plans_payment_step_3")}</li>
                                    <li>{t("plans_payment_step_4")}</li>
                                </S.PaymentStepsList>
                                <S.PaymentFootnote>{t("plans_payment_auto_confirm")}</S.PaymentFootnote>
                            </S.PaymentQrSection>
                        ) : (
                            <>
                                <S.PaymentHeading>{t("plans_payment_heading")}</S.PaymentHeading>
                                <S.PaymentPlanSummary>{planSummary}</S.PaymentPlanSummary>

                                <ul className="nav nav-tabs mb-4 mt-2 d-none" role="tablist">
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link ${paymentMethod === "PIX" ? "active" : ""}`}
                                            onClick={() => setPaymentMethod("PIX")}
                                            role="tab"
                                            style={{cursor: "pointer"}}
                                        >
                                            Pix
                                        </button>
                                    </li>
                                    <li className="nav-item d-none">
                                        <button
                                            type="button"
                                            className={`nav-link ${paymentMethod === "CREDIT" ? "active" : ""}`}
                                            onClick={() => setPaymentMethod("CREDIT")}
                                            role="tab"
                                            style={{cursor: "pointer"}}
                                        >
                                            Cartão de crédito
                                        </button>
                                    </li>
                                </ul>
                                <div className="tab-content">
                                    {paymentMethod === "PIX" && (
                                        <div className="tab-pane active">
                                            <Form
                                                ref={formPixRef}
                                                onSubmit={handleSubmitPaymentPix}
                                            >
                                                <S.PaymentPixFormBlock>
                                                    <InputMask
                                                        type="text"
                                                        name="cpfCnpj"
                                                        placeholder="000.000.000-00"
                                                        label={t("plans_payment_pix_label")}
                                                        mask={"cpfCnpj"}
                                                        className="payment-pix-field"
                                                        inputProps={{maxLength: 18}}
                                                    />
                                                    <CustomButton
                                                        fullWidth
                                                        type="submit"
                                                        loading={loading}
                                                        className="payment-pix-submit"
                                                        style={{
                                                            ...performancePrimaryButtonStyle,
                                                            width: "100%",
                                                        }}
                                                        onMouseEnter={buttonHoverIn}
                                                        onMouseLeave={buttonHoverOut}
                                                    >
                                                        {t("plans_payment_submit")}
                                                    </CustomButton>
                                                </S.PaymentPixFormBlock>
                                            </Form>
                                        </div>
                                    )}
                                    {paymentMethod === "CREDIT" && (
                                        <div className="tab-pane active d-none">
                                            <Form
                                                ref={formCardRef}
                                                onSubmit={async (data) => {
                                                    try {
                                                        if (!type || !isValidPlan) {
                                                            toast.warning(t("plans_payment_invalid"));
                                                            return;
                                                        }

                                                        setLoading(true);
                                                        if (formCardRef.current) {
                                                            formCardRef.current.setErrors({});
                                                        }

                                                        const schema = Yup.object().shape({
                                                            cpfCnpj: Yup.string().required(t("input_required")),
                                                            cardHolderName: Yup.string().required(t("input_required")),
                                                            cardNumber: Yup.string().required(t("input_required")),
                                                            cardExpiration: Yup.string().required(t("input_required")),
                                                            cardCvv: Yup.string().required(t("input_required")),
                                                        });

                                                        await schema.validate(data, {
                                                            abortEarly: false,
                                                        });

                                                        const formData = new FormData();
                                                        formData.append("type", type);
                                                        formData.append("cpfCnpj", data.cpfCnpj);
                                                        formData.append("cardHolderName", data.cardHolderName);
                                                        formData.append("cardNumber", data.cardNumber);
                                                        formData.append("cardExpiration", data.cardExpiration);
                                                        formData.append("cardCvv", data.cardCvv);

                                                        const response = await newSignaturePaymentCreditCard(formData);
                                                        if (response.data && response.data.signatureId) {
                                                            setIsPaymentConfirmed(true);
                                                            setTimeout(() => {
                                                                history.push("/dashboard");
                                                            }, 3000);
                                                        } else {
                                                            toast.error("Ocorreu um erro ao realizar o pagamento.");
                                                        }

                                                        setLoading(false);
                                                    } catch (err) {
                                                        setLoading(false);
                                                        if (err instanceof Yup.ValidationError) {
                                                            const validationErrors = {};
                                                            err.inner.forEach((error) => {
                                                                validationErrors[error.path] = error.message;
                                                            });
                                                            if (formCardRef.current) {
                                                                formCardRef.current.setErrors(validationErrors);
                                                            }
                                                            toast.warning(messageTypes.REQUIRED_FIELDS);
                                                            return;
                                                        }

                                                        const messageError = err?.response?.data?.messageError;
                                                        if (messageError) {
                                                            toast.error(messageError);
                                                        } else {
                                                            toast.error(messageTypes.ERROR);
                                                        }
                                                    }
                                                }}
                                            >
                                                <div className="row">
                                                    <div className="col-12 mb-4">
                                                        <InputMask
                                                            type="text"
                                                            name="cpfCnpj"
                                                            placeholder={"xxx.xxx.xxx-xx"}
                                                            label={"CPF/CNPJ do titular"}
                                                            mask={"cpfCnpj"}
                                                            size={"small"}
                                                            inputProps={{maxLength: 18}}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 mb-4">
                                                        <InputMask
                                                            type="text"
                                                            name="cardHolderName"
                                                            label="Nome no cartão"
                                                            size={"small"}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 mb-4">
                                                        <InputMask
                                                            type="text"
                                                            name="cardNumber"
                                                            label="Número do cartão"
                                                            size={"small"}
                                                            mask={"cardNumber"}
                                                            placeholder={"xxxx xxxx xxxx xxxx"}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-6 mb-4">
                                                        <InputMask
                                                            name="cardExpiration"
                                                            label="Validade"
                                                            mask="cardExpiration"
                                                            placeholder={"MM/AA"}
                                                            size={"small"}
                                                        />
                                                    </div>
                                                    <div className="col-6 mb-4">
                                                        <InputMask
                                                            placeholder={"xxx"}
                                                            name="cardCvv"
                                                            label="CVV"
                                                            mask="cardCvv"
                                                            size={"small"}
                                                            type="number"
                                                        />
                                                    </div>
                                                </div>
                                                <CustomButton
                                                    className="col-12 mt-4 font-weight-bold"
                                                    type="submit"
                                                    style={{...performancePrimaryButtonStyle, width: "100%", textTransform: "none"}}
                                                    loading={loading}
                                                >
                                                    Finalizar pagamento
                                                </CustomButton>
                                            </Form>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </S.PaymentCard>
                </S.PaymentPageInner>
                </S.PaymentContentShell>
            </S.PlansPaymentContent>
        </S.PlansPaymentLayout>
    );
};

export default PlansPayment;
